import React, { useEffect, useMemo, useState } from "react";
import { cn } from "../../../lib/utils";
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  FormActions,
  FormField,
  FormGrid,
  Input,
  SectionHeader,
  Select,
  Textarea,
} from "../../../shared/ui/composites";
import { useUpdateSettingJsonMutation } from "../api/react-query";
import {
  COMPARISONS,
  FUNCTIONS,
  METRIC_UNITS,
  OPERATORS,
  FORMULA_KINDS,
  QUERY_CATALOG,
  COMMISSION_COMPONENT_QUERIES,
  createEmptyFormula,
  defaultCommissionShape,
  denormalizeFormulaConfig,
  ensureUniqueFormulaKey,
  expressionToString,
  parseFormulaString,
  getFieldsForOutput,
  getOutputDefinitions,
  normalizeFormulaConfig,
  outputDefForQueryMetric,
  queryForOutput,
} from "../utils/formulaConfig";
import {
  FormulaChip,
  FormulaFieldButton,
  FormulaOperatorButton,
} from "../../../shared/ui/formula/FormulaChip";
import { fmtInt } from "../../../shared/utils/formatters";
import BandTable from "./BandTable";

const cardGrid = "grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4";
const operatorsGrid =
  "grid grid-cols-[repeat(auto-fit,minmax(72px,1fr))] gap-2";

/** Sample values so the builder can show numbers + operators while editing */
const SAMPLE_VALUES = {
  supervisor_sale_target: 1200,
  supervisor_customer_target: 80,
  seller_sale_target: 400,
  seller_customer_target: 25,
  supervisor_avg_metric: 900,
  seller_avg_metric: 300,
  supervisor_avg_carton: 900,
  seller_avg_carton: 300,
  supervisor_avg_qty: 1800,
  seller_avg_qty: 600,
  supervisor_avg_weight: 12,
  seller_avg_weight: 4,
  supervisor_avg_customer: 70,
  seller_avg_customer: 20,
  ratio_metric: 3,
  ratio_customer: 3.5,
  ratio_carton: 3,
  n_percent: 100,
  months_lookback: 3,
  total_sales: 850,
  sale_target: 1000,
  total_customers: 40,
  customer_target: 50,
  sale_pct: 85,
  customer_pct: 80,
  f_sale_target: 1000,
  f_customer_target: 50,
  achievement_gap_sale: 150,
  achievement_gap_customer: 10,
  actual_quantity: 85,
  basket_percent: 30,
  approved_amount: 280000000,
  sale_weight: 40,
  component_weight: 20,
  excess_percent: 0,
  max_payout_percent: 0,
  rial_cap_trigger_percent: 2.2,
  rial_cap_percent: 2,
  group_band_commission: 33600000,
  total_gross_carton: 100,
  net_amount: 160000000,
  net_pack_qty: 90,
  net_qty: 302,
  net_weight_kg: 42000,
  group_achievement: 85,
  group_allocated: 33600000,
  group_applied: 70,
  group_applied_capped: 70,
  group_excess: 0,
  basket_achievement: 72,
  entry_min_percent: 70,
  entry_gated: 1,
  entry_passed: 1,
  achievement: 85,
  customer_count_month: 178,
  customer_target_month: 300,
  financial_claims: 5000000000,
  return_cause_vocher_pack_qty: 4,
  return_cause_prize_vocher_pack_qty: 0,
  return_cause_sell_return_pack_qty: 1,
  return_qty: 5,
  return_weight_kg: 12,
  commission_sale: 41160000,
  commission_return: 0,
  commission_active: 0,
  commission_claims: 0,
};

function AccordionPanel({ title, open, onToggle, badge, children, preview }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-right"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-slate-100 text-xs text-slate-500">
            {open ? "▾" : "▸"}
          </span>
          {title}
          {badge ? <Badge variant="muted">{badge}</Badge> : null}
        </span>
      </button>
      {!open && preview ? (
        <pre
          dir="ltr"
          className="mb-0 max-h-16 overflow-hidden border-t border-slate-100 bg-slate-50 px-4 py-2 text-left font-mono text-[11px] leading-5 text-slate-500"
        >
          {preview}
        </pre>
      ) : null}
      {open ? <div className="border-t border-slate-100 px-4 py-3">{children}</div> : null}
    </div>
  );
}

function groupedOutputs(defs) {
  const groups = [];
  const map = new Map();
  defs.forEach((def) => {
    const name = def.group || "خروجی";
    if (!map.has(name)) {
      const g = { name, items: [] };
      map.set(name, g);
      groups.push(g);
    }
    map.get(name).items.push(def);
  });
  return groups;
}

function FormulaCardOutputs({ formula, settingsKey }) {
  const defs = getOutputDefinitions(settingsKey, formula.kind);
  const groups = groupedOutputs(defs);
  const [openGroup, setOpenGroup] = useState(null);

  return (
    <div className="mb-3 space-y-2">
      {groups.map((group) => {
        const preview = group.items
          .slice(0, 2)
          .map((def) => `${formula.outputs?.[def.key] || "—"}`)
          .join("\n");
        return (
          <AccordionPanel
            key={group.name}
            title={group.name}
            badge={`${group.items.length}`}
            open={openGroup === group.name}
            onToggle={() =>
              setOpenGroup((current) => (current === group.name ? null : group.name))
            }
            preview={preview}
          >
            <div className="space-y-2">
              {group.items.map((def) => (
                <div key={def.key}>
                  <div className="mb-1 text-xs font-semibold text-slate-500">{def.fa}</div>
                  <code className="block whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-2 text-xs text-slate-700 ltr text-left">
                    {formula.outputs?.[def.key] || "—"}
                  </code>
                </div>
              ))}
            </div>
          </AccordionPanel>
        );
      })}
    </div>
  );
}

function groupFields(fields) {
  return fields.reduce((acc, field) => {
    const category = field.category || "فیلدها";
    if (!acc[category]) acc[category] = [];
    acc[category].push(field);
    return acc;
  }, {});
}

function previewFormulaWithSamples(formula = "") {
  if (!formula || !String(formula).trim()) {
    return { tokens: [], substituted: "", result: null, error: null };
  }

  const tokens = String(formula)
    .replace(/([+\-*/()])/g, " $1 ")
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => {
      if (["+", "-", "*", "/", "(", ")"].includes(token)) {
        return { type: "operator", value: token, display: token };
      }
      if (!Number.isNaN(Number(token)) && token.trim() !== "") {
        return { type: "number", value: Number(token), display: String(token) };
      }
      const sample = SAMPLE_VALUES[token];
      const numeric = sample == null ? 0 : Number(sample);
      return {
        type: "field",
        value: token,
        display: String(numeric),
        label: token,
        missing: sample == null,
      };
    });

  let expression = String(formula).trim();
  const keys = Object.keys(SAMPLE_VALUES).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    expression = expression.replace(
      new RegExp(`\\b${key}\\b`, "g"),
      String(SAMPLE_VALUES[key]),
    );
  }
  expression = expression.replace(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g, "0");

  try {
    // eslint-disable-next-line no-new-func
    const result = Function(`'use strict'; return (${expression})`)();
    if (typeof result !== "number" || Number.isNaN(result) || !Number.isFinite(result)) {
      return { tokens, substituted: expression, result: 0, error: null };
    }
    return { tokens, substituted: expression, result, error: null };
  } catch (err) {
    return { tokens, substituted: expression, result: null, error: err.message };
  }
}

export default function FormulaSettingEditor({ setting, allSettings = [] }) {
  const settingsKey = setting?.settings_key || "";
  const updateMutation = useUpdateSettingJsonMutation();

  const [model, setModel] = useState(() =>
    normalizeFormulaConfig(setting?.settings_json || {}, settingsKey),
  );
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [activeOutput, setActiveOutput] = useState("sale_target");
  const [searchField, setSearchField] = useState("");
  const [message, setMessage] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [openBands, setOpenBands] = useState(false);
  const [openQueries, setOpenQueries] = useState(true);

  const outputDefs = useMemo(
    () => getOutputDefinitions(settingsKey, draft?.kind),
    [settingsKey, draft?.kind],
  );

  useEffect(() => {
    setModel(normalizeFormulaConfig(setting?.settings_json || {}, settingsKey));
    setShowBuilder(false);
    setEditingKey(null);
    setDraft(null);
    setActiveOutput(getOutputDefinitions(settingsKey)[0]?.key || "sale_target");
    setMessage("");
  }, [setting?.settings_key, setting?.settings_json, settingsKey]);

  const fieldCatalog = useMemo(
    () =>
      getFieldsForOutput(setting, {
        kind: draft?.kind,
        formula: draft,
        outputKey: activeOutput,
      }),
    [setting, draft, activeOutput],
  );

  const filteredFields = useMemo(() => {
    const source = searchField
      ? fieldCatalog.filter(
          (field) =>
            field.fa?.includes(searchField) ||
            field.en?.toLowerCase().includes(searchField.toLowerCase()) ||
            field.key.toLowerCase().includes(searchField.toLowerCase()),
        )
      : fieldCatalog;
    return groupFields(source);
  }, [fieldCatalog, searchField]);

  const persistModel = async (nextModel, successMessage) => {
    const payload = denormalizeFormulaConfig(nextModel, {
      preserveLegacy: true,
      settingsKey,
    });

    try {
      await updateMutation.mutateAsync({
        settingsKey: setting.settings_key,
        settings_json: payload,
      });
      setModel(nextModel);
      setMessage(successMessage || "ذخیره با موفقیت انجام شد");
      return true;
    } catch (err) {
      setMessage(err?.response?.data?.error || err.message || "خطا در ذخیره");
      return false;
    }
  };

  const syncPrimaryFromOutputs = (nextDraft) => {
    const primaryKey = outputDefs[0]?.key;
    if (!primaryKey) return nextDraft;
    return {
      ...nextDraft,
      formula: nextDraft.outputs?.[primaryKey] || "",
      expression: nextDraft.expressions?.[primaryKey] || [],
    };
  };

  const openCreate = () => {
    const defaultKind = settingsKey === "commission_calc_formula" ? "commission" : "suggestion";
    const empty = createEmptyFormula(
      model.formulas.map((item) => item.key),
      settingsKey,
      defaultKind,
    );
    setEditingKey(null);
    setDraft(empty);
    setActiveOutput(getOutputDefinitions(settingsKey, defaultKind)[0]?.key || "sale_target");
    setSearchField("");
    setShowBuilder(true);
    setOpenQueries(true);
    setOpenBands(defaultKind === "commission");
    setMessage("");
  };

  const openEdit = (formula) => {
    setEditingKey(formula.key);
    setDraft({
      ...formula,
      outputs: { ...(formula.outputs || {}) },
      expressions: Object.fromEntries(
        Object.entries(formula.expressions || {}).map(([key, value]) => [
          key,
          Array.isArray(value) ? [...value] : [],
        ]),
      ),
      queries: { ...(formula.queries || {}) },
      tables: { ...(formula.tables || {}) },
      entry_condition: { ...(formula.entry_condition || {}) },
      rial_cap_trigger_percent: formula.rial_cap_trigger_percent ?? 2.2,
      rial_cap_percent: formula.rial_cap_percent ?? 2,
    });
    const defs = getOutputDefinitions(settingsKey, formula.kind);
    setActiveOutput(defs[0]?.key || "sale_target");
    setSearchField("");
    setShowBuilder(true);
    setOpenQueries(true);
    setOpenBands(false);
    setMessage("");
  };

  const handleCancel = () => {
    setShowBuilder(false);
    setEditingKey(null);
    setDraft(null);
  };

  const handleSetActive = async (key) => {
    await persistModel(
      { ...model, active_formula: key },
      `فرمول فعال به «${key}» تغییر کرد`,
    );
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`آیا از حذف فرمول «${key}» مطمئن هستید؟`)) return;

    const nextFormulas = model.formulas.filter((item) => item.key !== key);
    const nextActive =
      model.active_formula === key
        ? nextFormulas[0]?.key || ""
        : model.active_formula;

    await persistModel(
      {
        ...model,
        formulas: nextFormulas,
        active_formula: nextActive,
      },
      "فرمول حذف شد",
    );
  };

  const updateActiveExpression = (expression) => {
    setDraft((current) => {
      const next = {
        ...current,
        outputs: {
          ...(current.outputs || {}),
          [activeOutput]: expressionToString(expression),
        },
        expressions: {
          ...(current.expressions || {}),
          [activeOutput]: expression,
        },
      };
      return syncPrimaryFromOutputs(next);
    });
  };

  const pushToken = (token) => {
    const currentExpression = draft?.expressions?.[activeOutput] || [];
    updateActiveExpression([...currentExpression, token]);
  };

  const handleAddField = (field) => {
    pushToken({
      type: "field",
      value: field.key,
      label: field.fa || field.key,
    });
  };

  const handleAddOperator = (symbol) => {
    pushToken({ type: "operator", value: symbol });
  };

  const handleAddFunction = (symbol) => {
    pushToken({ type: "function", value: symbol, label: symbol });
  };

  const handleAddNumber = () => {
    const number = window.prompt("عدد را وارد کنید:");
    if (number == null || number === "") return;
    const parsed = Number(number);
    if (Number.isNaN(parsed)) {
      window.alert("مقدار وارد شده عدد نیست");
      return;
    }
    pushToken({ type: "number", value: parsed });
  };

  const handleRemoveToken = (index) => {
    const currentExpression = draft?.expressions?.[activeOutput] || [];
    updateActiveExpression(currentExpression.filter((_, i) => i !== index));
  };

  const handleClearExpression = () => {
    updateActiveExpression([]);
  };

  const handleDropReorder = (toIndex) => {
    if (dragIndex == null || dragIndex === toIndex) return;
    const currentExpression = [...(draft?.expressions?.[activeOutput] || [])];
    const [moved] = currentExpression.splice(dragIndex, 1);
    currentExpression.splice(toIndex, 0, moved);
    updateActiveExpression(currentExpression);
    setDragIndex(null);
  };

  const handleSaveFormula = async () => {
    if (!draft) return;

    if (!draft.title?.trim()) {
      window.alert("لطفاً عنوان فرمول را وارد کنید");
      return;
    }

    const hasAnyOutput = outputDefs.some((def) => {
      const formula = draft.outputs?.[def.key];
      const expression = draft.expressions?.[def.key];
      return (formula && formula.trim()) || (expression && expression.length > 0);
    });

    if (!hasAnyOutput) {
      window.alert("حداقل یک خروجی فرمول را تعریف کنید");
      return;
    }

    const existingKeys = model.formulas.map((item) => item.key);
    const nextKey = ensureUniqueFormulaKey(
      draft.key || draft.title,
      existingKeys,
      editingKey,
    );

    const nextFormula = syncPrimaryFromOutputs({
      ...draft,
      key: nextKey,
      formula_type: draft.formula_type || nextKey,
      months_lookback: Number(draft.months_lookback) || 3,
      metric_unit: draft.metric_unit || "carton",
    });

    let nextFormulas;
    if (editingKey == null) {
      nextFormulas = [...model.formulas, nextFormula];
    } else {
      nextFormulas = model.formulas.map((item) =>
        item.key === editingKey ? nextFormula : item,
      );
    }

    const nextActive =
      model.active_formula === editingKey
        ? nextKey
        : model.active_formula || nextKey;

    const ok = await persistModel(
      {
        ...model,
        formulas: nextFormulas,
        active_formula: nextActive,
      },
      "فرمول ذخیره شد",
    );

    if (ok) handleCancel();
  };

  if (!setting) return null;

  if (showBuilder && draft) {
    const activeExpression = draft.expressions?.[activeOutput] || [];
    const activeFormulaText = draft.outputs?.[activeOutput] || "";
    const samplePreview = previewFormulaWithSamples(activeFormulaText);

    return (
      <div className="w-full min-w-0 space-y-4">
        <SectionHeader
          title={editingKey ? "ویرایش فرمول" : "ایجاد فرمول جدید"}
          action={
            <Button variant="cancel" onClick={handleCancel}>
              بازگشت
            </Button>
          }
        />

        <Card variant="panel" padding="lg">
          <CardTitle className="mb-4">اطلاعات فرمول</CardTitle>
          <FormGrid>
            <FormField label="عنوان" required>
              <Input
                variant="form"
                value={draft.title}
                onChange={(e) =>
                  setDraft((current) => ({ ...current, title: e.target.value }))
                }
                placeholder="مثال: نسبت میانگین سرپرست به فروشنده"
              />
            </FormField>
            <FormField label="کلید فرمول" required>
              <Input
                variant="form"
                dir="ltr"
                className="ltr text-left"
                value={draft.key}
                onChange={(e) =>
                  setDraft((current) => ({ ...current, key: e.target.value }))
                }
              />
            </FormField>
            <FormField label="نوع فرمول" hint="پیشنهاد تارگت، محاسبه پورسانت، یا فرمول وظیفه">
              <Select
                value={draft.kind || "suggestion"}
                onChange={(e) => {
                  const kind = e.target.value;
                  const defs = getOutputDefinitions(settingsKey, kind);
                  setDraft((current) => {
                    const outputs = { ...(current.outputs || {}) };
                    const expressions = { ...(current.expressions || {}) };
                    defs.forEach((def) => {
                      if (!outputs[def.key]) {
                        outputs[def.key] = def.defaultFormula || "";
                        expressions[def.key] = parseFormulaString(def.defaultFormula || "");
                      }
                    });
                    const next = { ...current, kind, outputs, expressions };
                    if (kind === "commission" && !Object.keys(next.queries || {}).length) {
                      const seeded = defaultCommissionShape();
                      next.queries = seeded.queries;
                      next.tables = { ...(next.tables || {}), ...seeded.tables };
                      next.entry_condition = next.entry_condition?.min_percent
                        ? next.entry_condition
                        : seeded.entry_condition;
                    }
                    return next;
                  });
                  setActiveOutput(defs[0]?.key || activeOutput);
                  setOpenBands(false);
                  setOpenQueries(kind === "commission");
                }}
              >
                {FORMULA_KINDS.map((kind) => (
                  <option key={kind.value} value={kind.value}>
                    {kind.label}
                  </option>
                ))}
              </Select>
            </FormField>
            {(draft.kind || "suggestion") === "suggestion" && (
              <FormField label="بازه میانگین (ماه)">
                <Input
                  variant="form"
                  type="number"
                  min={1}
                  max={24}
                  value={draft.months_lookback}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      months_lookback: e.target.value,
                    }))
                  }
                />
              </FormField>
            )}
            {(draft.kind || "suggestion") === "suggestion" && settingsKey !== "dashboard_formula" && (
              <FormField
                label="واحد معیار (فقط پیش‌فرض)"
                hint="در پیشنهاد تارگت، واحد از personnel_target یا محصول در targetgroups خوانده می‌شود (کارتن/ظرف/تناژ). این مقدار فقط وقتی واحد تعریف نشده باشد استفاده می‌شود."
              >
                <Select
                  value={draft.metric_unit || "carton"}
                  onChange={(e) =>
                    setDraft((current) => ({
                      ...current,
                      metric_unit: e.target.value,
                    }))
                  }
                >
                  {METRIC_UNITS.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <FormField label="توضیح فارسی">
              <Input
                variant="form"
                value={draft.fa_description}
                onChange={(e) =>
                  setDraft((current) => ({
                    ...current,
                    fa_description: e.target.value,
                  }))
                }
              />
            </FormField>
            <FormField label="توضیح انگلیسی">
              <Input
                variant="form"
                value={draft.description}
                onChange={(e) =>
                  setDraft((current) => ({
                    ...current,
                    description: e.target.value,
                  }))
                }
              />
            </FormField>
          </FormGrid>
        </Card>

        {draft.kind === "commission" && (
          <>
            <AccordionPanel
              title="کوئری‌های محاسبه"
              open={openQueries}
              onToggle={() => setOpenQueries((v) => !v)}
              badge={`${Object.keys(draft.queries || {}).length || COMMISSION_COMPONENT_QUERIES.length} کوئری`}
              preview={COMMISSION_COMPONENT_QUERIES.map((row) => {
                const key = draft.queries?.[row.tableKey];
                const q = QUERY_CATALOG.find((item) => item.key === key);
                return `${row.fa}: ${q?.fa || key || "—"}`;
              }).join("\n")}
            >
              <div className="grid gap-3 md:grid-cols-2">
                {COMMISSION_COMPONENT_QUERIES.map((row) => {
                  const options = QUERY_CATALOG.filter((q) => row.queryKeys.includes(q.key));
                  const value = draft.queries?.[row.tableKey] || options[0]?.key || "";
                  return (
                    <FormField key={row.tableKey} label={row.fa}>
                      <Select
                        value={value}
                        onChange={(e) => {
                          const query_key = e.target.value;
                          const q = QUERY_CATALOG.find((item) => item.key === query_key);
                          setDraft((current) => {
                            const next = {
                              ...current,
                              queries: { ...(current.queries || {}), [row.tableKey]: query_key },
                              tables: {
                                ...(current.tables || {}),
                                [row.tableKey]: {
                                  ...(current.tables?.[row.tableKey] || {}),
                                  query_key,
                                },
                              },
                            };
                            const boundOut = outputDefForQueryMetric(row.tableKey);
                            if (boundOut && q?.formula) {
                              next.outputs = { ...(next.outputs || {}), [boundOut.key]: q.formula };
                              next.expressions = {
                                ...(next.expressions || {}),
                                [boundOut.key]: parseFormulaString(q.formula),
                              };
                            }
                            return next;
                          });
                          const boundOut = outputDefForQueryMetric(row.tableKey);
                          if (boundOut) setActiveOutput(boundOut.key);
                        }}
                      >
                        {options.map((q) => (
                          <option key={q.key} value={q.key}>
                            {q.fa}
                          </option>
                        ))}
                      </Select>
                    </FormField>
                  );
                })}
              </div>
              <p className="mb-0 mt-3 text-xs text-slate-500">
                فیلدهای سازنده از کوئری همان خروجی می‌آیند. عوض کردن کوئری، عبارت خروجی متصل (تحقق گروه، مرجوعی، اکتیو، مطالبات) را هم عوض می‌کند.
              </p>
            </AccordionPanel>

            <AccordionPanel
              title="جداول باند و شرط ورود"
              open={openBands}
              onToggle={() => setOpenBands((v) => !v)}
              badge={`${Object.keys(draft.tables || {}).length} جدول`}
            >
              <div className="mb-4 grid gap-4 xl:grid-cols-2">
                {COMMISSION_COMPONENT_QUERIES.map((row) => {
                  const table = draft.tables?.[row.tableKey] || {
                    key: row.tableKey,
                    title: row.fa,
                    bands: [],
                  };
                  return (
                    <BandTable
                      key={row.tableKey}
                      table={{ ...table, title: table.title || row.fa }}
                      onChange={(next) =>
                        setDraft((current) => ({
                          ...current,
                          tables: { ...(current.tables || {}), [row.tableKey]: next },
                        }))
                      }
                    />
                  );
                })}
              </div>
              <FormField label="حداقل درصد تحقق سبد برای شرط ورود">
                <label className="flex max-w-xs items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    variant="form"
                    value={draft.entry_condition?.min_percent ?? 70}
                    onChange={(e) =>
                      setDraft((current) => ({
                        ...current,
                        entry_condition: {
                          ...(current.entry_condition || {}),
                          min_percent: Number(e.target.value),
                          applies_to: ["active_target", "return_pct", "claims"],
                        },
                      }))
                    }
                  />
                  <span>%</span>
                </label>
              </FormField>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <FormField
                  label="آستانه سقف ریالی"
                  hint="اگر پورسانت باند / فروش ریالی گروه از این درصد بیشتر شود، سقف اعمال می‌شود. پیش‌فرض اکسل ۲٫۲٪"
                >
                  <label className="flex max-w-xs items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      variant="form"
                      value={draft.rial_cap_trigger_percent ?? 2.2}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          rial_cap_trigger_percent: Number(e.target.value),
                          fields: {
                            ...(current.fields || {}),
                            rial_cap_trigger_percent: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <span>%</span>
                  </label>
                </FormField>
                <FormField
                  label="سقف پرداخت ریالی"
                  hint="درصد فروش ریالی گروه که در صورت عبور از آستانه پرداخت می‌شود. پیش‌فرض اکسل ۲٪"
                >
                  <label className="flex max-w-xs items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      variant="form"
                      value={draft.rial_cap_percent ?? 2}
                      onChange={(e) =>
                        setDraft((current) => ({
                          ...current,
                          rial_cap_percent: Number(e.target.value),
                          fields: {
                            ...(current.fields || {}),
                            rial_cap_percent: Number(e.target.value),
                          },
                        }))
                      }
                    />
                    <span>%</span>
                  </label>
                </FormField>
              </div>
            </AccordionPanel>
          </>
        )}

        <Card variant="panel" padding="lg">
          <CardTitle className="mb-3">خروجی‌های فرمول</CardTitle>
          <p className="mb-4 text-sm text-slate-500">
            هر خروجی یک مرحله محاسبه است. فیلدهای تراشه از کوئری همان خروجی می‌آیند؛ با عوض کردن کوئری، عبارت هم به‌روز می‌شود.
          </p>

          <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
            <div className="space-y-3 lg:sticky lg:top-2 lg:max-h-[70vh] lg:overflow-y-auto">
              {groupedOutputs(outputDefs).map((group) => (
                <div key={group.name}>
                  <div className="mb-1 text-[11px] font-semibold text-slate-400">{group.name}</div>
                  <div className="flex flex-col gap-1">
                    {group.items.map((def) => {
                      const boundQuery = QUERY_CATALOG.find(
                        (q) => q.key === queryForOutput(def, draft.queries),
                      );
                      return (
                        <Button
                          key={def.key}
                          type="button"
                          size="sm"
                          variant={activeOutput === def.key ? "submit" : "secondary"}
                          className="h-auto min-h-8 flex-col items-stretch justify-start py-1.5 text-right"
                          onClick={() => setActiveOutput(def.key)}
                        >
                          <span>{def.fa || def.key}</span>
                          {boundQuery ? (
                            <span className="text-[10px] font-normal opacity-80">
                              {boundQuery.fa}
                            </span>
                          ) : null}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div>
              {(() => {
                const activeDef = outputDefs.find((d) => d.key === activeOutput);
                const boundQuery = QUERY_CATALOG.find(
                  (q) => q.key === queryForOutput(activeDef, draft.queries),
                );
                return boundQuery ? (
                  <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                    فیلدهای این خروجی از کوئری «{boundQuery.fa}» می‌آیند.
                  </p>
                ) : null;
              })()}

          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h4 className="m-0 text-sm font-bold text-slate-700">
              سازنده عبارت:{" "}
              {outputDefs.find((d) => d.key === activeOutput)?.fa || activeOutput}
            </h4>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={handleAddNumber}>
                افزودن عدد
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={handleClearExpression}>
                پاک کردن
              </Button>
            </div>
          </div>

          <div
            dir="ltr"
            className="mb-4 min-h-[72px] rounded-[12px] border border-dashed border-[#c7d2fe] bg-[#f8fafc] p-3 text-left"
          >
            {activeExpression.length === 0 ? (
              <p className="m-0 text-right text-sm text-slate-400" dir="rtl">
                روی فیلد/عملگر کلیک کنید. برای جابجایی آیتم‌ها را بکشید.
              </p>
            ) : (
              <div className="flex flex-wrap justify-start gap-2">
                {activeExpression.map((item, index) => (
                  <FormulaChip
                    key={`${item.type}-${item.value}-${index}`}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropReorder(index)}
                    className={cn(
                      "cursor-grab active:cursor-grabbing",
                      item.type === "field" && "bg-[#eef2ff] !text-[#3730a3]",
                      item.type === "operator" && "bg-[#ecfeff] !text-[#0e7490]",
                      item.type === "function" && "bg-[#fef3c7] !text-[#92400e]",
                      item.type === "number" && "bg-[#f1f5f9] !text-[#334155]",
                    )}
                  >
                    <span>{item.label || item.value}</span>
                    <button
                      type="button"
                      className="border-0 bg-transparent p-0 text-xs text-slate-500"
                      onClick={() => handleRemoveToken(index)}
                    >
                      ✕
                    </button>
                  </FormulaChip>
                ))}
              </div>
            )}
          </div>

          <FormField label="عبارت نهایی این خروجی">
            <Textarea
              dir="ltr"
              className="ltr text-left font-mono text-sm"
              rows={3}
              value={activeFormulaText}
              onChange={(e) => {
                const value = e.target.value;
                setDraft((current) =>
                  syncPrimaryFromOutputs({
                    ...current,
                    outputs: {
                      ...(current.outputs || {}),
                      [activeOutput]: value,
                    },
                    expressions: {
                      ...(current.expressions || {}),
                      [activeOutput]: parseFormulaString(value),
                    },
                  }),
                );
              }}
            />
          </FormField>

          {activeFormulaText?.trim() && (
            <div className="mt-3 rounded-[12px] border border-[#dbeafe] bg-[#eff6ff] px-3 py-3">
              <div className="mb-2 text-sm font-bold text-[#1e3a8a]">
                پیش‌نمایش با اعداد نمونه (برای فهم فرمول)
              </div>
              <div className="mb-2 flex flex-wrap items-center justify-start gap-1.5" dir="ltr">
                {samplePreview.tokens.map((token, idx) => (
                  <span
                    key={`preview-${idx}-${token.value}`}
                    title={
                      token.type === "field"
                        ? `${token.label}${token.missing ? " (نمونه ندارد → 0)" : ""}`
                        : token.value
                    }
                    className={cn(
                      "inline-flex min-h-[28px] items-center rounded-md px-2 py-0.5 text-xs font-bold",
                      token.type === "operator" && "bg-[#ecfeff] text-[#0e7490]",
                      token.type === "number" && "bg-[#f1f5f9] text-[#334155]",
                      token.type === "field" &&
                        (token.missing
                          ? "bg-[#fee2e2] text-[#991b1b]"
                          : "bg-[#eef2ff] text-[#3730a3]"),
                    )}
                  >
                    {token.type === "field"
                      ? fmtInt(token.display)
                      : token.display}
                  </span>
                ))}
                <span className="mx-1 text-slate-400">=</span>
                <span className="inline-flex min-h-[28px] items-center rounded-md bg-[#dcfce7] px-2 py-0.5 text-xs font-bold text-[#166534]">
                  {samplePreview.error
                    ? "خطا"
                    : fmtInt(samplePreview.result || 0)}
                </span>
              </div>
              <div className="ltr text-left font-mono text-xs text-slate-500">
                {samplePreview.substituted}
                {samplePreview.error ? ` | ${samplePreview.error}` : ""}
              </div>
              <p className="mb-0 mt-2 text-xs text-slate-500">
                این اعداد نمونه هستند. در صفحه تارگت‌ها مقادیر واقعی فروشنده/سرپرست
                نمایش داده می‌شود.
              </p>
            </div>
          )}

          <div className="mt-4 space-y-4">
            <div dir="ltr">
              <h4 className="mb-2 text-right text-sm font-bold text-slate-700" dir="rtl">عملگرها</h4>
              <div className={operatorsGrid}>
                {OPERATORS.map((op) => (
                  <FormulaOperatorButton
                    key={op.symbol}
                    onClick={() => handleAddOperator(op.symbol)}
                    title={op.label}
                  >
                    {op.symbol}
                  </FormulaOperatorButton>
                ))}
              </div>
            </div>

            <div dir="ltr">
              <h4 className="mb-2 text-right text-sm font-bold text-slate-700" dir="rtl">مقایسه</h4>
              <div className={operatorsGrid}>
                {COMPARISONS.map((op) => (
                  <FormulaOperatorButton
                    key={op.symbol}
                    onClick={() => handleAddOperator(op.symbol)}
                    title={op.label}
                  >
                    {op.symbol}
                  </FormulaOperatorButton>
                ))}
              </div>
            </div>

            <div dir="ltr">
              <h4 className="mb-2 text-right text-sm font-bold text-slate-700" dir="rtl">توابع</h4>
              <div className={operatorsGrid}>
                {FUNCTIONS.map((fn) => (
                  <FormulaOperatorButton
                    key={fn.symbol}
                    onClick={() => handleAddFunction(fn.symbol)}
                    title={fn.label}
                  >
                    {fn.symbol}
                  </FormulaOperatorButton>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h4 className="m-0 text-sm font-bold text-slate-700">
                  فیلدهای کوئری و پارامترها
                </h4>
                <Input
                  variant="search"
                  className="max-w-[220px]"
                  placeholder="جستجوی فیلد..."
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                />
              </div>

              {Object.keys(filteredFields).length === 0 ? (
                <EmptyState
                  className="border-0 shadow-none py-6"
                  message="فیلدی یافت نشد"
                />
              ) : (
                Object.entries(filteredFields).map(([category, fields]) => (
                  <div key={category} className="mb-4">
                    <div className="mb-2 text-xs font-semibold text-slate-500">
                      {category}
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2">
                      {fields.map((field) => (
                        <FormulaFieldButton
                          key={field.key}
                          onClick={() => handleAddField(field)}
                        >
                          <span className="text-[0.8rem] !text-slate-800">
                            {field.fa}
                          </span>
                          <span className="ltr text-left text-[0.7rem] font-medium !text-slate-400">
                            {field.key}
                            {SAMPLE_VALUES[field.key] != null
                              ? ` = ${fmtInt(SAMPLE_VALUES[field.key])}`
                              : ""}
                          </span>
                        </FormulaFieldButton>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
            </div>
          </div>

          <FormActions>
            <Button type="button" variant="cancel" onClick={handleCancel}>
              انصراف
            </Button>
            <Button
              type="button"
              variant="submit"
              onClick={handleSaveFormula}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "در حال ذخیره..." : "ذخیره فرمول"}
            </Button>
          </FormActions>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-4">
      <SectionHeader
        title={setting.title || setting.settings_key}
        action={
          <Button variant="add" onClick={openCreate}>
            افزودن فرمول
          </Button>
        }
      />

      {setting.description && (
        <p className="m-0 text-sm text-slate-500">{setting.description}</p>
      )}

      <Card variant="panel" padding="lg">
        <FormGrid className="mb-0">
          <FormField label="فرمول فعال">
            <Select
              value={model.active_formula || ""}
              onChange={(e) => handleSetActive(e.target.value)}
            >
              <option value="">انتخاب کنید</option>
              {model.formulas.map((formula) => (
                <option key={formula.key} value={formula.key}>
                  {formula.title || formula.key}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="یادداشت">
            <Input
              variant="form"
              value={model.notes || ""}
              onChange={(e) =>
                setModel((current) => ({ ...current, notes: e.target.value }))
              }
              onBlur={() => persistModel(model, "یادداشت ذخیره شد")}
            />
          </FormField>
        </FormGrid>
        {message && <p className="mt-3 mb-0 text-sm text-slate-600">{message}</p>}
      </Card>

      {model.formulas.length === 0 ? (
        <EmptyState
          message="هنوز فرمولی ثبت نشده است"
          actionLabel="ایجاد اولین فرمول"
          onAction={openCreate}
        />
      ) : (
        <div className={cardGrid}>
          {model.formulas.map((formula) => {
            const isActive = model.active_formula === formula.key;
            return (
              <Card key={formula.key} variant="panel" padding="lg">
                <CardHeader className="mb-2 p-0">
                  <CardTitle>{formula.title || formula.key}</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="icon"
                      size="icon"
                      title="ویرایش"
                      onClick={() => openEdit(formula)}
                    >
                      ✏️
                    </Button>
                    <Button
                      variant="icon"
                      size="icon"
                      title="حذف"
                      onClick={() => handleDelete(formula.key)}
                    >
                      🗑️
                    </Button>
                  </div>
                </CardHeader>

                {(formula.fa_description || formula.description) && (
                  <CardDescription>
                    {formula.fa_description || formula.description}
                  </CardDescription>
                )}

                <div className="mb-3 flex flex-wrap gap-2">
                  <Badge variant={isActive ? "default" : "secondary"}>
                    {isActive ? "فعال" : "غیرفعال"}
                  </Badge>
                  <Badge variant="scope">{formula.key}</Badge>
                  {formula.kind && (
                    <Badge variant="muted">
                      {FORMULA_KINDS.find((k) => k.value === formula.kind)?.label || formula.kind}
                    </Badge>
                  )}
                  {formula.kind === "suggestion" && (
                    <Badge variant="muted">
                      {fmtInt(formula.months_lookback || 3)} ماه
                    </Badge>
                  )}
                  {formula.kind === "suggestion" && settingsKey !== "dashboard_formula" && (
                    <Badge variant="muted">
                      {formula.metric_unit || "carton"}
                    </Badge>
                  )}
                  {Object.values(formula.queries || {}).filter(Boolean).map((queryKey) => (
                    <Badge key={queryKey} variant="muted">
                      {QUERY_CATALOG.find((q) => q.key === queryKey)?.fa || queryKey}
                    </Badge>
                  ))}
                </div>

                <FormulaCardOutputs formula={formula} settingsKey={settingsKey} />

                {!isActive && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSetActive(formula.key)}
                  >
                    فعال‌سازی
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
